import axios from 'axios';
import { Modal } from 'antd';
import { REFRESH_API_PATH } from 'constants/api';
import authService from '../shared/service/authService';
import { getActionSessionId } from '../shared/utils/actionSession';

/**
 * 백엔드 표준 에러 응답 포맷: { code, message, errors }
 * errors 객체의 필드별 에러 메시지를 사용자에게 모달로 표시
 */
const showApiErrorModal = (data: { code?: string; message?: string; errors?: Record<string, string[]> }) => {
  const lines: string[] = [];

  if (data.errors && typeof data.errors === 'object') {
    Object.entries(data.errors).forEach(([field, messages]) => {
      if (Array.isArray(messages)) {
        messages.forEach(msg => lines.push(`${field}: ${msg}`));
      }
    });
  }

  Modal.error({
    title: data.message || 'Error',
    content: lines.length > 0 ? lines.join('\n') : undefined,
    okText: 'Confirm',
  });
};

axios.interceptors.request.use(config => {
  const token = localStorage.getItem('authToken');
  if (config.url!.includes('s3')) return config;

  // ODX Admin API 호출은 인터셉터 제외
  if (config.url?.includes(process.env.REACT_APP_ODX_ADMIN_BASE_URL || '')) {
    return config;
  }

  config.withCredentials = true;
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // Action Session ID 헤더 추가
  const sessionId = getActionSessionId();
  if (sessionId) {
    config.headers['X-Action-Session-Id'] = sessionId;
  }

  return config;
});

axios.interceptors.response.use(
  async response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;
    if (!error.response) {
      throw error;
    }

    if (error.response.status === 401) {
      originalRequest._retry = true;
      if (!(originalRequest.url as string).includes(REFRESH_API_PATH)) {
        try {
          const refreshResponse = await authService.refreshToken();
          localStorage.setItem('authToken', refreshResponse.access);
          return axios(originalRequest);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (refreshError) {
          await authService.clearAuthInfo();
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = '/login?redirect=' + encodeURIComponent(currentPath);
        }
      }
      await authService.clearAuthInfo();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = '/login?redirect=' + encodeURIComponent(currentPath);
    }

    // 백엔드 표준 에러 포맷({ code, message, errors })인 경우 모달 표시
    const data = error.response?.data;
    if (data && typeof data === 'object' && data.code && data.message) {
      showApiErrorModal(data);
    }

    throw error;
  }
);
